import { useEffect } from "react";
import { useSyncExternalStore } from "react";
import { FX_RATES, fxRate } from "../constants";
import { setDisplayCurrency, subscribeCurrency, getCurrencySnapshot } from "../utils/formatters";
import { useAuth } from "./AuthContext";

// =============================================================================
// Currency owner — per-USER display currency (USD ⇄ AUD / SGD / …)
// =============================================================================
// All money in the DB is USD (Dynamis banks a USD Wise balance; no per-row
// currency column). This flips the DISPLAY currency only — never mutates stored
// data, never writes back; the "$" symbol is unchanged in every currency (Mako
// 2026-07-15: "just show the $ amount, the sidebar toggle tells you which").
//
// PER-USER, NOT per-org: a user's profiles.display_currency decides what THEY
// see. role 'client' DEFAULTS into their currency (the client lives in it, e.g.
// Rayston → SGD); everyone else defaults USD and can TOGGLE into it (staff work
// in USD). NULL display_currency = USD only, no toggle. Currency follows WHO is
// looking, not WHOSE data — so a Dynamis rep on a client's account stays USD.
// (Mako 2026-07-16: "I don't care if the reps see SGD, this is just for the client.")
//
// FAST + SMOOTH: state lives in a tiny external store in utils/formatters.ts.
// Components subscribe via useSyncExternalStore, so a change re-renders every
// dollar IN PLACE — no page remount, no chart re-animation, scroll preserved.
// PageWrapper subscribes (re-renders the tree); memo'd money components subscribe
// directly since memo blocks the cascade.
//
// RATE SOURCE: hardcoded FX_RATES map in constants.ts. No DB, no live FX (CSP
// blocks it). To onboard a currency: add to FX_RATES + set display_currency.
// =============================================================================

export type CurrencyMode = string; // ISO 4217 code; "USD" is canonical (rate 1)

// localStorage key — the "altara_currency" prefix is safe-listed in
// lib/cacheVersion.ts so a /cachebump does NOT reset the user's manual choice.
const MODE_KEY = "altara_currency_mode";

/** A code is "known" if it's USD (canonical) or has a rate. Guards stale/garbage localStorage. */
function isKnownCode(code: string | null): code is string {
  return !!code && (code === "USD" || !!FX_RATES[code]);
}

function readStored(): string | null {
  try {
    const v = localStorage.getItem(MODE_KEY);
    return isKnownCode(v) ? v : null;
  } catch {
    return null;
  }
}

function persist(code: string): void {
  try {
    localStorage.setItem(MODE_KEY, code);
  } catch {
    /* preference just won't persist across reloads */
  }
}

// Initialize the store at module load — BEFORE React's first paint — from the
// last stored code, so formatUSD/formatCompact convert correctly on the very
// first render with no flash. CurrencyResolver corrects it once the user loads.
const _initial = readStored() ?? "USD";
setDisplayCurrency(_initial, fxRate(_initial));

/** The non-USD currency a user may switch into (their display_currency, if supported). */
export function userTargetCurrency(displayCurrency: string | null | undefined): string | null {
  return displayCurrency && FX_RATES[displayCurrency] ? displayCurrency : null;
}

/** Apply a currency code app-wide (persists + notifies all subscribers). */
export function setCurrencyMode(code: string): void {
  persist(code);
  setDisplayCurrency(code, fxRate(code));
}

/**
 * Subscribe a component to currency changes so it re-renders in place on change.
 * PageWrapper calls this to re-render the whole tree; memo'd money components
 * call it directly (memo blocks the cascade from PageWrapper).
 */
export function useCurrencyMode(): CurrencyMode {
  const snap = useSyncExternalStore(subscribeCurrency, getCurrencySnapshot, getCurrencySnapshot);
  return snap.split(":")[0];
}

interface CurrencyState {
  code: CurrencyMode; // active display code
  rate: number; // active rate (1 for USD)
  asOf: string | null; // provenance date for the active non-USD code
  target: string | null; // the current user's non-USD option (for the toggle)
  options: string[]; // ["USD"] or ["USD", target]
  canToggle: boolean; // there IS a non-USD option to flip to
  setMode: (code: string) => void;
  toggle: () => void;
}

/** Read the active currency + the current user's toggle options (subscribes the caller). */
export function useCurrency(): CurrencyState {
  const code = useCurrencyMode();
  const { user } = useAuth();
  const target = userTargetCurrency(user?.displayCurrency);
  const options = target ? ["USD", target] : ["USD"];
  const asOf = code !== "USD" && FX_RATES[code] ? FX_RATES[code].asOf : null;
  return {
    code,
    rate: fxRate(code),
    asOf,
    target,
    options,
    canToggle: options.length > 1,
    setMode: setCurrencyMode,
    toggle: () => {
      if (!target) return;
      setCurrencyMode(code === "USD" ? target : "USD");
    },
  };
}

/**
 * Resolves the active display currency from the EFFECTIVE user and applies it.
 * Mount ONCE inside AuthProvider (renders null). Resolution:
 *   • role 'client'  → default to their display_currency (client lives in it).
 *   • everyone else  → default USD (staff toggle into it).
 *   • a valid manual choice in localStorage (USD or the user's target) wins.
 * Uses the EFFECTIVE user (viewAs-aware): "View As Rayston" shows Rayston's SGD,
 * not the exec's own currency (Mako 2026-07-16). When not impersonating, the
 * effective user IS the real user, so normal sessions are unaffected. The
 * allowed-set guard also stops one identity's stored choice bleeding into the
 * next (a rep after a client sees USD, not the client's SGD).
 */
export function CurrencyResolver(): null {
  const { user, realUser } = useAuth();
  const displayCurrency = user?.displayCurrency;
  const role = user?.role;
  const id = user?.id;
  const impersonating = !!(user && realUser && user.id !== realUser.id);
  useEffect(() => {
    const target = userTargetCurrency(displayCurrency);
    const defaultCode = role === "client" && target ? target : "USD";
    if (impersonating) {
      // Faithful POV: always the impersonated identity's DEFAULT. Ignore the real
      // user's stored choice (it's not the target's preference) and don't persist
      // — otherwise the exec's "USD" leaks over the client's SGD default.
      setDisplayCurrency(defaultCode, fxRate(defaultCode));
      return;
    }
    const allowed = target ? ["USD", target] : ["USD"];
    const stored = readStored();
    const active = stored && allowed.includes(stored) ? stored : defaultCode;
    setCurrencyMode(active);
  }, [displayCurrency, role, id, impersonating]);
  return null;
}
